/**
 * The code printed on the page a presenter holds up at the end of a session.
 *
 * It names the session and nothing else. It is printed on paper, taped to a
 * wall or held over a presenter's head in front of forty people, so it is not
 * treated as a secret anywhere: who was present is known from the signed-in
 * account that sends the tap, never from possession of the code.
 *
 * The square on the page carries the fast path. These characters carry the slow
 * one, for a camera that will not focus and for a teacher typing it into her
 * voucher screen that night.
 */

/**
 * Crockford's base32 alphabet: the digits and letters, less `I`, `L`, `O` and
 * `U`. The first three are dropped because a person reading `1` and `I` off
 * paper under fluorescent light cannot tell which they are looking at, and `U`
 * because eight random characters occasionally spell something a teacher should
 * not have to read out.
 */
export const PRESENCE_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** Eight characters: 32^8, which is a thousand million million times more than one convention needs. */
export const PRESENCE_CODE_LENGTH = 8;

/**
 * What somebody typed, turned into what the database holds — or `null` if it
 * cannot be one of our codes at all.
 *
 * Forgiving on purpose. Case is ignored. `I` and `L` become `1`, `O` becomes
 * `0`, because those are the substitutions a person makes when reading the
 * printed alphabet back. Spaces and hyphens are dropped, so the grouped form on
 * the page can be typed exactly as it is printed.
 */
export function normalizePresenceCode(raw: string): string | null {
  const folded = raw
    .toUpperCase()
    .replace(/[IL]/gu, "1")
    .replace(/O/gu, "0")
    .replace(/[^0-9A-Z]/gu, "");
  if (folded.length !== PRESENCE_CODE_LENGTH) return null;
  for (const character of folded) {
    if (!PRESENCE_CODE_ALPHABET.includes(character)) return null;
  }
  return folded;
}

/** A code drawn from random bytes. One byte per character; the alphabet divides 256 evenly. */
export function presenceCodeFrom(bytes: Uint8Array): string {
  if (bytes.length < PRESENCE_CODE_LENGTH) {
    throw new Error("A presence code needs at least eight random bytes.");
  }
  let code = "";
  for (let index = 0; index < PRESENCE_CODE_LENGTH; index += 1) {
    code += PRESENCE_CODE_ALPHABET[(bytes[index] as number) % PRESENCE_CODE_ALPHABET.length];
  }
  return code;
}

/** How the code is printed: two groups of four, which is how people read a code aloud. */
export function formatPresenceCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/**
 * The address the square on the page points at.
 *
 * The code goes after the `#`, which browsers never send to a server, copying
 * what the device-link squares already do. It keeps the code out of request
 * logs, out of referrer headers, and out of anything a proxy writes down.
 */
export function presenceLinkFor(origin: string, code: string): string {
  return `${origin.replace(/\/+$/u, "")}/present#c=${code}`;
}
